import useIsLoggedIn from '@customer/hooks/useIsLoggedIn';
import { useStoreActions, useStoreState } from '@customer/hooks/useStore';
import { calculateTotals } from '@customer/services/coveService';
import store from '@customer/state/store';
import config from '@customer/utils/config';
import datoLoad from '@customer/utils/datoLoad';
import { COUNTRY } from '@shared/cove-ui';
import { CartProvider, useCart } from '@shared/cart-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

const saveCart = async (cartId, body) => {
  await store.persist.resolveRehydration();
  const isLoggedIn = store.getState()?.user?.isLoggedIn;
  const accountGuid = store.getState()?.activeId?.currentId;

  if (
    !isLoggedIn ||
    !accountGuid ||
    window.location.pathname.toLowerCase() !== '/equipment/order/'
  ) {
    return body;
  }

  const coveCreditBalance =
    store.getState()?.account?.coveCreditBalance?.data?.[accountGuid || '']
      ?.coveCreditBalance;

  const coveCredit = (() => {
    if (
      !body?.applyCoveCreditAmount ||
      Number.isNaN(parseFloat(`${body?.applyCoveCreditAmount}`)) ||
      Number.isNaN(parseFloat(`${coveCreditBalance}`))
    ) {
      return 0;
    }

    return Math.max(
      0,
      Math.min(
        Number(parseFloat(`${coveCreditBalance}`).toFixed(2)),
        Number(parseFloat(`${body.applyCoveCreditAmount}`).toFixed(2))
      )
    );
  })();

  const result = await calculateTotals(
    {
      accountGuid,
      cartProducts: Object.values(body.cartProducts),
      promoCode: body.promoCode,
      couponCodes: body.couponCodes,
      coveCredit
    },
    {
      accessToken: await store.getActions()?.pando?.getToken?.()
    }
  );

  if (result?.data) {
    result.data.cartId = cartId;
  }
  return result;
};

const SyncCart: React.FC<{}> = () => {
  const isLoggedIn = useIsLoggedIn();
  const accountGuid = useStoreState((state) => state?.activeId?.currentId);
  const cart = useCart();
  useEffect(() => {
    if (isLoggedIn) {
      cart?.saveCart?.();
    }
  }, [accountGuid, isLoggedIn, !cart]);
  const [cartAccountGuid, setCartAccountGuid] = useState(accountGuid);
  useEffect(() => {
    if (!cartAccountGuid && cartAccountGuid !== accountGuid) {
      setCartAccountGuid(accountGuid);
    }

    if (cartAccountGuid && cartAccountGuid !== accountGuid) {
      cart?.clear?.();
      setCartAccountGuid(accountGuid);
    }
  }, [cartAccountGuid, setCartAccountGuid, accountGuid]);

  return null;
};

const CustomerCartProvider: React.FC<{ partnerId: string }> = ({
  partnerId,
  children,
  ...props
}) => {
  return (
    <CartProvider
      partnerId={partnerId}
      apiV4Url={config.COVE_API_V4_URL}
      datoData={datoLoad({ partnerId, country: COUNTRY })}
      saveCart={saveCart}
      shippingMethod="Regular"
      country={COUNTRY}
      prefixLocalStorageKey={`CUSTOMER_PORTAL_`}
    >
      <SyncCart />
      {children}
    </CartProvider>
  );
};

export default CustomerCartProvider;
